// src/components/Musteriler.js

import React, { useState, useMemo } from 'react';
import CustomerFormModal from '../components/modals/CustomerFormModal';
import { logDeviceAction } from '../modules/devices';
import { db } from '../firebase';
import { collection, doc, deleteDoc, addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canDeleteCustomer } from '../utils/validation';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage, normalizeSerial } from '../utils/deviceDedup';
import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Stack, useTheme, useMediaQuery, IconButton } from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import DevicesIcon from '@mui/icons-material/Devices';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import { formatCurrency } from '../utils/formatters';
import { exportCustomerList } from '../modules/customer/utils/exportCustomerList';
import usePermissions from '../hooks/usePermissions';
import useCachedCollection from '../hooks/useCachedCollection';

const Musteriler = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'uyku', 'cihaz'
  const [visibleCount, setVisibleCount] = useState(25); // Faz 7: ekranda gösterilen satır sayısı
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canDelete } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Faz 7: realtime onSnapshot yerine oturum-içi cache (her koleksiyon oturumda 1
  // kez okunur, ekranlar arası geçişte tekrar okunmaz). serviceRecords/transactions
  // bakiye için, devices ise seri no araması + export için TÜM olarak gerekir.
  // Yazma işlemlerinden sonra ilgili koleksiyon refresh edilir; "Yenile" ile elle de tazelenir.
  const { data: customers, loading: customersLoading, refresh: refreshCustomers } = useCachedCollection('customers');
  const { data: serviceRecords, loading: serviceLoading, refresh: refreshService } = useCachedCollection('serviceRecords');
  const { data: transactions, loading: transactionsLoading, refresh: refreshTransactions } = useCachedCollection('transactions');
  const { data: devices, loading: devicesLoading, refresh: refreshDevices } = useCachedCollection('devices');
  const loading = customersLoading || serviceLoading || transactionsLoading || devicesLoading;

  const handleRefresh = () => {
    refreshCustomers();
    refreshService();
    refreshTransactions();
    refreshDevices();
  };

  const customerFinancials = useMemo(() => {
    const financials = {};
    customers.forEach(customer => {
      financials[customer.id] = { ...customer, totalRevenue: 0, totalPaid: 0, balance: 0 };
    });

    serviceRecords.forEach(record => {
      if (financials[record.musteriId]) {
        financials[record.musteriId].totalRevenue += record.toplamTutar || 0;
        financials[record.musteriId].totalPaid += record.odenenTutar || 0;
      }
    });

    transactions.forEach(t => {
      if (financials[t.customerId]) {
        if (t.type === 'borc') financials[t.customerId].totalRevenue += t.amount || 0;
        else if (t.type === 'tahsilat') financials[t.customerId].totalPaid += t.amount || 0;
      }
    });

    Object.values(financials).forEach(fin => { fin.balance = fin.totalRevenue - fin.totalPaid; });
    // Cache'ten gelen müşteriler sırasız olabilir; eski Firestore orderBy("adSoyad")
    // davranışını korumak için ada göre sırala.
    return Object.values(financials).sort((a, b) => (a.adSoyad || '').localeCompare(b.adSoyad || '', 'tr'));
  }, [customers, serviceRecords, transactions]);

  // Müşteri id -> o müşteriye ait cihazların seri numaraları (gelişmiş aramada
  // "seri no" ile müşteri bulabilmek için). Cihaz listesi değişince yeniden kurulur.
  const customerSerialsMap = useMemo(() => {
    const map = {};
    devices.forEach(device => {
      if (!device.musteriId) return;
      const normalized = normalizeSerial(device.seriNumarasi);
      if (!normalized) return;
      if (!map[device.musteriId]) map[device.musteriId] = [];
      map[device.musteriId].push(normalized);
    });
    return map;
  }, [devices]);

  const handleAddCustomer = async (customerData, deviceInfo) => {
    try {
      // Mükerrer cihaz denetimi: aynı seri no zaten varsa müşteri DE oluşturulmadan
      // uyarılır (yarım kayıt/orphan müşteri oluşmaz).
      if (deviceInfo) {
        const duplicate = await findDuplicateDeviceBySerial(deviceInfo.seriNumarasi);
        if (duplicate) {
          alert(duplicateDeviceMessage(duplicate, deviceInfo.seriNumarasi));
          return;
        }
      }

      const customerRef = await addDoc(collection(db, 'customers'), {
        ...customerData,
        createdAt: Timestamp.now()
      });

      // Cihaz bilgisi varsa ekle
      if (deviceInfo) {
        const deviceData = {
          ...deviceInfo,
          musteriId: customerRef.id,
          musteriAdSoyad: customerData.adSoyad,
          createdAt: Timestamp.now()
        };
        const deviceRef = await addDoc(collection(db, 'devices'), deviceData);

        // Log Device Action
        await logDeviceAction(
          deviceRef.id,
          'create',
          'Yeni cihaz oluşturuldu (Müşteri oluşturulurken)',
          currentUser?.uid || 'system',
          deviceData
        );
      }

      // Faz 7: realtime yok — yeni müşteri/cihazın listede hemen görünmesi için cache tazele.
      await refreshCustomers();
      if (deviceInfo) await refreshDevices();
    } catch (error) {
      console.error("Müşteri eklenirken hata:", error);
      throw error;
    }
  };

  const handleUpdateCustomer = async (id, updatedData) => {
    try {
      await updateDoc(doc(db, 'customers', id), {
        ...updatedData,
        updatedAt: Timestamp.now()
      });
      await refreshCustomers(); // Faz 7: güncellemeyi listede hemen yansıt
    } catch (error) {
      console.error("Müşteri güncellenirken hata:", error);
      throw error;
    }
  };

  const handleDeleteCustomer = async (e, id, adSoyad) => {
    e.stopPropagation();
    const result = await canDeleteCustomer(id);
    if (!result.canDelete) {
      alert(result.reason);
      return;
    }
    if (window.confirm(`'${adSoyad}' adlı müşteriyi kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      try {
        await deleteDoc(doc(db, 'customers', id));
        await refreshCustomers(); // Faz 7: silinen müşteri listeden hemen düşsün
        alert("Müşteri başarıyla silindi.");
      } catch (e) {
        console.error("Müşteri silinirken hata: ", e);
        alert("Müşteri silinirken bir hata oluştu.");
      }
    }
  };

  const handleOpenEditModal = (e, customer) => {
    e.stopPropagation();
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  // Gelişmiş arama: ad soyad, TC kimlik no, telefon, e-posta ve müşterinin
  // cihazlarının seri numaraları üzerinde arar. Tüm alanlar null-güvenli okunur
  // (adSoyad boş olsa bile çökmez). Telefon için rakam-bazlı (boşluk/tire duyarsız)
  // ek karşılaştırma; seri no için cihaz dedup ile aynı normalize mantığı kullanılır.
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const searchDigits = normalizedSearch.replace(/\D/g, '');
  const searchSerial = normalizeSerial(searchTerm);

  const matchesSearchTerm = (customer) => {
    if (!normalizedSearch) return true;
    const adSoyad = (customer.adSoyad || '').toLowerCase();
    const tcKimlikNo = (customer.tcKimlikNo || '').toLowerCase();
    const telefon = (customer.telefon || '').toLowerCase();
    const email = (customer.email || '').toLowerCase();

    if (adSoyad.includes(normalizedSearch)) return true;
    if (tcKimlikNo.includes(normalizedSearch)) return true;
    if (telefon.includes(normalizedSearch)) return true;
    if (email.includes(normalizedSearch)) return true;
    if (searchDigits && (customer.telefon || '').replace(/\D/g, '').includes(searchDigits)) return true;
    if (searchSerial) {
      const serials = customerSerialsMap[customer.id] || [];
      if (serials.some(s => s.includes(searchSerial))) return true;
    }
    return false;
  };

  const filteredCustomers = customerFinancials.filter(customer => {
    const matchesSearch = matchesSearchTerm(customer);

    if (filterType === 'all') return matchesSearch;
    const services = Array.isArray(customer.hizmetTuru) ? customer.hizmetTuru : [customer.hizmetTuru];
    if (filterType === 'uyku') return matchesSearch && services.includes('Uyku Hastası');
    if (filterType === 'cihaz') return matchesSearch && (services.includes('Cihaz Satışı') || services.includes('Kiralama') || services.includes('Cihaz Sahibi'));
    return matchesSearch;
  });

  // Faz 7: ekranda yalnızca ilk `visibleCount` müşteriyi göster (DOM'u hafifletir).
  // Arama/filtre TÜM müşteriler üzerinde çalışır; export da tüm filtreli veriyi indirir.
  const visibleCustomers = filteredCustomers.slice(0, visibleCount);

  // Excel export (salt-okunur) — aktif filtreye uyan müşterileri indirir
  const handleExportVisible = () => {
    if (filteredCustomers.length === 0) {
      alert('Dışa aktarılacak müşteri bulunamadı.');
      return;
    }
    exportCustomerList({ customers: filteredCustomers, devices, serviceRecords, scopeLabel: 'Goruntulenen' });
  };

  // Excel export (salt-okunur) — filtreden bağımsız tüm müşterileri indirir
  const handleExportAll = () => {
    if (customerFinancials.length === 0) {
      alert('Dışa aktarılacak müşteri bulunamadı.');
      return;
    }
    exportCustomerList({ customers: customerFinancials, devices, serviceRecords, scopeLabel: 'Tumu' });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1 } }}>
      <PageHeader
        title="Müşteri Yönetimi"
        subtitle="Müşteri listesi ve yönetim işlemleri"
        icon={<PeopleIcon />}
      >
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={(e, newFilter) => { if (newFilter) setFilterType(newFilter); }}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              <ToggleButton value="all"><PeopleIcon sx={{ mr: 0.5 }} /> Tümü</ToggleButton>
              <ToggleButton value="uyku"><HotelIcon sx={{ mr: 0.5 }} /> Uyku Hastaları</ToggleButton>
              <ToggleButton value="cihaz"><DevicesIcon sx={{ mr: 0.5 }} /> Cihaz Sahipleri</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Ad, TC, telefon, e-posta veya seri no ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              sx={{ bgcolor: 'rgba(255,255,255,0.05)', minWidth: { xs: '100%', sm: 280 } }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Yenile
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportVisible}
              disabled={loading}
            >
              Görüneni İndir
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportAll}
              disabled={loading}
            >
              Tümünü İndir
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddModal}>Yeni Müşteri Ekle</Button>
      </PageHeader>

      <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0, bgcolor: isMobile ? 'transparent' : undefined, boxShadow: isMobile ? 'none' : undefined, backdropFilter: isMobile ? 'none' : undefined, border: isMobile ? 'none' : undefined }}>
        {loading ? (
          <Loading message="Müşteriler yükleniyor..." />
        ) : isMobile ? (
          // MOBILE CARD VIEW
          <Stack spacing={2}>
            {visibleCustomers.map((customer) => (
              <GlassCard key={customer.id} onClick={() => navigate(`/musteri/${customer.id}`)} sx={{ p: 2, cursor: 'pointer', '&:active': { transform: 'scale(0.98)' }, transition: 'transform 0.1s' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">{customer.adSoyad}</Typography>
                    <Typography variant="body2" color="text.secondary">{customer.telefon}</Typography>
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: (customer.balance || 0) > 0 ? 'error.main' : 'success.main' }}>
                    {formatCurrency(customer.balance)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  {customer.hizmetTuru ? (
                    (Array.isArray(customer.hizmetTuru) ? customer.hizmetTuru : [customer.hizmetTuru]).slice(0, 3).map((tur, idx) => (
                      <StatusChip key={idx} label={tur} color={tur === 'Uyku Hastası' ? 'info' : 'default'} sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                    ))
                  ) : '-'}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }} onClick={(e) => e.stopPropagation()}>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={(e) => handleOpenEditModal(e, customer)}>Düzenle</Button>
                  {canDelete() && (
                    <IconButton size="small" color="error" onClick={(e) => handleDeleteCustomer(e, customer.id, customer.adSoyad)}><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Box>
              </GlassCard>
            ))}
            {filteredCustomers.length === 0 && (
              <EmptyState
                icon={<PeopleIcon />}
                message="Müşteri bulunamadı."
                description={searchTerm || filterType !== 'all' ? 'Arama/filtre kriterlerinizle eşleşen müşteri yok.' : 'Yeni bir müşteri ekleyerek başlayın.'}
              />
            )}
            {visibleCount < filteredCustomers.length && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                <Typography variant="caption" color="text.secondary">{visibleCustomers.length} / {filteredCustomers.length} gösteriliyor</Typography>
                <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
              </Box>
            )}
          </Stack>
        ) : (
          // DESKTOP TABLE VIEW
          <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Adı Soyadı</TableCell>
                  <TableCell>Hizmet Türü</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell align="right">Bakiye</TableCell>
                  <TableCell>E-posta</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCustomers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <EmptyState
                        icon={<PeopleIcon />}
                        message="Müşteri bulunamadı."
                        description={searchTerm || filterType !== 'all' ? 'Arama/filtre kriterlerinizle eşleşen müşteri yok.' : 'Yeni bir müşteri ekleyerek başlayın.'}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {visibleCustomers.map((customer) => (
                  <TableRow hover key={customer.id} onClick={() => navigate(`/musteri/${customer.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{customer.adSoyad}</TableCell>
                    <TableCell>
                      {customer.hizmetTuru ? (
                        (Array.isArray(customer.hizmetTuru) ? customer.hizmetTuru : [customer.hizmetTuru]).map((tur, idx) => (
                          <StatusChip
                            key={idx}
                            label={tur}
                            color={tur === 'Uyku Hastası' ? 'info' : tur === 'Cihaz Satışı' ? 'success' : 'default'}
                            icon={tur === 'Uyku Hastası' ? <HotelIcon /> : <DevicesIcon />}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))
                      ) : '-'}
                    </TableCell>
                    <TableCell>{customer.telefon}</TableCell>
                    <TableCell sx={{ color: (customer.bakiye || 0) > 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                      {formatCurrency(customer.balance)}
                    </TableCell>
                    <TableCell>{customer.email || '-'}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" startIcon={<EditIcon />} sx={{ mr: 1 }} onClick={(e) => handleOpenEditModal(e, customer)}>Düzenle</Button>
                      {canDelete() && (
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={(e) => handleDeleteCustomer(e, customer.id, customer.adSoyad)}>Sil</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && !isMobile && visibleCount < filteredCustomers.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
            <Typography variant="caption" color="text.secondary">{visibleCustomers.length} / {filteredCustomers.length} gösteriliyor</Typography>
            <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
          </Box>
        )}
      </GlassCard>
      <CustomerFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingCustomer(null); }}
        onAddCustomer={handleAddCustomer}
        onUpdateCustomer={handleUpdateCustomer}
        customerToEdit={editingCustomer}
      />
    </Box>
  );
};

export default Musteriler;
